import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckSquare, Maximize2, Loader2, Clock, Pause, CheckCircle,
} from 'lucide-react';
import { apiRequest, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const PRIORITY_COLORS = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-blue-100 text-blue-700 border-blue-200',
  low: 'bg-slate-100 text-slate-700 border-slate-200',
};

// Same 3-status simplified model as the Tasks page.
const TILES = [
  { key: 'pending',   label: 'Pending',   Icon: Clock,       cls: 'bg-amber-50 text-amber-700' },
  { key: 'on_hold',   label: 'On Hold',   Icon: Pause,       cls: 'bg-orange-50 text-orange-700' },
  { key: 'completed', label: 'Completed', Icon: CheckCircle, cls: 'bg-emerald-50 text-emerald-700' },
];

export default function MyTasksWidget({ expandable = true }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [stats, setStats] = useState({ pending: 0, on_hold: 0, completed: 0 });
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [list, statsData] = await Promise.all([
        apiRequest('/task-management/tasks?assigned_to_me=true&status=pending&limit=5'),
        apiRequest('/task-management/tasks/stats'),
      ]);
      setTasks(list?.tasks || []);
      const me = statsData?.assigned_to_me || {};
      setStats({
        pending: me.pending || 0,
        on_hold: me.on_hold || 0,
        completed: me.completed || 0,
      });
    } catch (err) {
      console.error('MyTasksWidget fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 120000); // refresh every 2 min
    return () => clearInterval(interval);
  }, [fetchAll]);

  const goToTasks = () => navigate('/task-management');

  return (
    <Card data-testid="my-tasks-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckSquare className="h-5 w-5 text-purple-500" />
            <CardTitle className="text-lg">My Tasks</CardTitle>
            {stats.pending > 0 && <Badge variant="secondary">{stats.pending}</Badge>}
          </div>
          {expandable && (
            <Button
              variant="ghost"
              size="icon"
              onClick={goToTasks}
              className="h-8 w-8"
              title="View all tasks"
              data-testid="my-tasks-expand-btn"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* 3 mini tiles — clickable to drill into the Tasks page */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {TILES.map(({ key, label, Icon, cls }) => (
                <button
                  key={key}
                  type="button"
                  onClick={goToTasks}
                  className={`text-center p-2 rounded-lg ${cls} hover:opacity-90 transition-opacity`}
                  data-testid={`my-tasks-tile-${key}`}
                >
                  <div className="flex items-center justify-center gap-1">
                    <Icon className="h-3.5 w-3.5" />
                    <p className="text-lg font-bold leading-none" data-testid={`my-tasks-tile-${key}-value`}>
                      {stats[key]}
                    </p>
                  </div>
                  <p className="text-[10px] mt-1">{label}</p>
                </button>
              ))}
            </div>

            {tasks.length === 0 ? (
              <div className="text-center py-4 text-slate-400">
                <CheckCircle className="h-10 w-10 mx-auto mb-2 text-emerald-300" />
                <p className="text-sm">All caught up!</p>
                <p className="text-xs mt-1">No pending tasks</p>
              </div>
            ) : (
              <div className="space-y-2">
                {tasks.map((task) => {
                  const isOverdue = task.due_date && new Date(task.due_date) < new Date();
                  return (
                    <div
                      key={task.id}
                      className={`p-2.5 rounded-lg border cursor-pointer hover:border-slate-300 transition-colors ${
                        isOverdue ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                      }`}
                      onClick={() => navigate(`/task-management/${task.id}`)}
                      data-testid={`my-tasks-item-${task.id}`}
                    >
                      <div className="flex items-start gap-2">
                        <Clock className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-500" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-900 line-clamp-1">{task.title}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {task.due_date && (
                              <span className={`flex items-center gap-1 text-xs ${
                                isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'
                              }`}>
                                <Clock className="h-3 w-3" />
                                {formatDate(task.due_date)}
                              </span>
                            )}
                            {task.priority && (
                              <Badge
                                variant="outline"
                                className={`text-[10px] px-1.5 py-0 ${PRIORITY_COLORS[task.priority] || ''}`}
                              >
                                {task.priority}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {stats.pending > tasks.length && (
                  <Button
                    variant="link"
                    size="sm"
                    className="w-full text-xs"
                    onClick={goToTasks}
                    data-testid="my-tasks-view-all-btn"
                  >
                    View all {stats.pending} pending tasks
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
